import React, { useEffect, useMemo } from 'react'
import styled from 'styled-components'
import { Splash } from '../components/Splash'
import { CountdownTimer } from '../components/Countdown/CountdownTimer'
import { GJLogo } from '../components/GJKit/newGJLogo'
import { useParams } from 'react-router-dom'
import { usePostHogAnalytics } from '@gojiraf/analytics'
import { useStore } from '../hooks/useStore'
import { WithCountdown } from '../hooks/use-countdown'
import { useTranslation } from 'react-i18next'
import { useEventById } from '../hooks/use-event-by-id'
import { BackgroundImageWithFallback } from '../components/BackgroundImageWithFallback'
import fallbackBackground from '../../assets/fallback_background.jpeg'
import { StoreLogoWithFallback } from '../components/StoreLogoWithFallback'
import { FitText } from '@gojiraf/responsive'
import { useSearchParams } from 'react-router-dom'
import { TopNavBar } from '../components/Call/TopNavBar'
import AskNameDialog from '../components/dialogs/AskNameDialog'
import { useAuth } from '@gojiraf/auth'
import { v4 } from 'uuid'

export function ScheduledEvent() {
  const { i18n } = useTranslation()
  const { postHogTrackEvent } = usePostHogAnalytics()
  const { storeId, eventId } = useParams()
  const [searchParams] = useSearchParams()
  const isIframe = searchParams.get('iframe') === 'true'
  const { data: event, isLoading: isLoadingEvent, error: eventError, refetch } = useEventById()
  const { data: store, isLoading: isLoadingStore, error: storeError } = useStore()
  const { setEventId: setAuthEventId, eventId: authStoredEventId, clearAuth, setCreds } = useAuth()

  useEffect(() => {
    if (!storeId || !eventId) return

    postHogTrackEvent('event-go-from-countdown')
  }, [storeId, eventId])

  useEffect(() => {
    if (store) {
      i18n.changeLanguage(store.lang)
    }
  }, [store])

  useEffect(() => {
    if (eventId && eventId !== authStoredEventId) {
      if (authStoredEventId) {
        clearAuth()
        setCreds({ id: v4() })
      }
      setAuthEventId(eventId)
    }
  }, [eventId, authStoredEventId])

  const timezone = useMemo(() => {
    const { timeZone } = new Intl.DateTimeFormat().resolvedOptions()

    const parts = timeZone.replace('_', ' ').split('/')

    return parts[parts.length - 1]
  }, [])

  const getFormatedEventDate = () => {
    const date = new Date(event.startAt ?? event.joinAt)
    const day = date.getDate()
    const month = date.toLocaleString('default', { month: 'long' })
    const hours = String(date.getHours()).padStart(2, '0')
    const minutes = String(date.getMinutes()).padStart(2, '0')
    const formatedEventDate = `${day} ${month.toUpperCase()} | ${hours}:${minutes} hs ${timezone}`

    return formatedEventDate
  }

  const handleFinish = async () => {
    await refetch()
  }

  if (isLoadingEvent || isLoadingStore) {
    return <Splash />
  }

  if (eventError || storeError) {
    return <div>Error loading event data</div>
  }

  return (
    <WithCountdown key={event.joinAt} finishAt={event.secondsUntilJoin} onFinish={handleFinish}>
      <BackgroundImageWithFallback
        desktop={event.images?.['countdown-desktop']}
        mobile={event.images?.['countdown-mobile']}
        fallback={fallbackBackground}
      >
        {isIframe && (
          <NavBarContainer>
            <TopNavBar isIframe={isIframe} />
          </NavBarContainer>
        )}
        <CountDownContainer>
          <StoreLogoWithFallback src={store.logoUrl} alt={'Store logo'} />
          <CustomText>
            <FitText min={20}>{'Live Shopping'}</FitText>
          </CustomText>
          <CustomText fontSize="1.613rem">
            <FitText min={11}>{getFormatedEventDate()}</FitText>
          </CustomText>
          <CountdownTimer />
          <StyledGJLogo color="white" />
        </CountDownContainer>
      </BackgroundImageWithFallback>
      <AskNameDialog />
    </WithCountdown>
  )
}

const StyledGJLogo = styled(GJLogo)`
  filter: drop-shadow(black 0rem 0.3rem 0.3rem);
`

const NavBarContainer = styled.div`
  position: absolute;
  top: 1rem;
  right: 0.5rem;
  display: flex;
  justify-content: flex-end;
  z-index: 10;
`

const CountDownContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  /* border-box: la app corre en content-box (default); sin esto,
     width:100% + padding desborda y genera scrollbar horizontal en Windows */
  box-sizing: border-box;
  width: 100%;
  max-height: 100%;
  overflow-y: auto;
  padding: 1rem;
`
const CustomText = styled.span`
  text-shadow: black 0.1rem 0.1rem 0.2rem;
  font-size: ${({ fontSize }) => fontSize ?? '3.625rem'};
  font-weight: 700;
  color: #ffffff;
  /* Sin margen lateral: con FitText el texto crece hasta el ancho maximo
     y el margen (aunque sea 4px) genera desborde horizontal en el container */
  margin: 4px 0;
  text-align: center;
  max-width: 100%;
  min-width: 0;
`
